import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { useUpdateOrgMemberRole } from '../hooks/useUpdateOrgMemberRole'
import type { OrgMember } from '../api/operations'

interface RemoveRoleDialogProps {
  member: OrgMember | null
  onOpenChange: (open: boolean) => void
}

/**
 * Quitar el rol de organización (goal portal-roles-2): el miembro conserva la
 * membresía pero la matriz de roles le cierra los datos del portal. Viaja como
 * `newRole: null` de la misma mutación de cambio de rol.
 *
 * goal portal-correcciones-1: como el listado solo trae a quien tiene rol,
 * quitarlo hace DESAPARECER la fila. Se avisa antes de confirmar y se dice cómo
 * revertirlo (invitarlo de nuevo por su correo).
 */
export function RemoveRoleDialog({ member, onOpenChange }: RemoveRoleDialogProps) {
  const { updateRole, loading } = useUpdateOrgMemberRole({
    onSuccess: () => onOpenChange(false),
  })

  const handleConfirm = async () => {
    if (!member) return
    await updateRole({ memberId: member.id, newRole: null })
  }

  return (
    <Dialog open={!!member} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Quitar rol</DialogTitle>
          <DialogDescription>
            {member ? (
              <>
                Vas a quitarle el rol a <strong>{member.email}</strong>. Seguirá siendo
                miembro de tu organización, pero dejará de ver sus datos.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {member ? (
          <p className="rounded-card border border-line bg-surface p-3 text-sm text-ink-muted">
            <strong className="text-ink">Además desaparecerá de esta lista</strong>, que
            solo muestra a los miembros con rol. Para devolvérselo, invítalo otra vez con
            su correo desde «Invitar miembro»: no se crea una cuenta nueva, solo se le
            asigna el rol.
          </p>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? 'Quitando…' : 'Quitar rol'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
